import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, type To } from "react-router-dom";

type HistoryTo =
  | To
  | {
      pathname?: string;
      search?: string;
      hash?: string;
      state?: unknown;
    };

const normalizeTarget = (to: HistoryTo, state?: unknown) => {
  if (typeof to === "string") {
    return { to, state };
  }

  const { state: stateInTo, ...path } = to as {
    hash?: string;
    pathname?: string;
    search?: string;
    state?: unknown;
  };
  return {
    to: path as To,
    state: state ?? stateInTo,
  };
};

const getPathnameFromHash = () => {
  const hash = window.location.hash || "";
  const match = hash.match(/#\/(?<pathname>[^?|^#]*)/)?.groups?.pathname;
  return match ? `/${match}` : "";
};

const getSearchFromHash = () => {
  const hash = window.location.hash || "";
  const match = hash.match(/\?(?<search>[^#]*)/)?.groups?.search;
  return match ? `?${match}` : "";
};

const getFragment = () => {
  const hash = window.location.hash || "";
  const match = hash.match(/[^/]#(?<hash>.*)$/)?.groups?.hash;
  return match ? `#${match}` : "";
};

const getCompatLocation = <
  TLocation extends {
    hash: string;
    pathname: string;
    search: string;
  },
>(
  location: TLocation
) => ({
  ...location,
  pathname: getPathnameFromHash() || location.pathname,
  search: getSearchFromHash() || location.search,
  hash: getFragment() || location.hash,
});

// This hook preserves the small subset of React Router v5 history behavior
// that the app still depends on while the codebase runs on v6 navigation APIs.
// It keeps existing push/replace/go/listen call sites working and normalizes
// HashRouter locations into pathname/search/hash values that match the old
// history-shaped expectations used throughout the app and tests. It is a
// temporary compatibility layer, and call sites should be migrated to
// useNavigate over time.
/**
 * @deprecated Temporary compatibility hook for React Router v5 history-style
 * APIs. Prefer useNavigate for new code and migrate existing call sites over
 * time.
 */
export const useHistoryCompat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listenersRef = useRef(
    new Set<(nextLocation: typeof location) => void>()
  );
  const compatLocation = useMemo(() => getCompatLocation(location), [location]);
  const previousLocationRef = useRef(compatLocation);

  useEffect(() => {
    if (previousLocationRef.current === compatLocation) {
      return;
    }

    previousLocationRef.current = compatLocation;
    for (const listener of listenersRef.current) {
      listener(compatLocation);
    }
  }, [compatLocation]);

  const push = useCallback(
    (to: HistoryTo, state?: unknown) => {
      const next = normalizeTarget(to, state);
      navigate(next.to, { state: next.state });
    },
    [navigate]
  );

  const replace = useCallback(
    (to: HistoryTo, state?: unknown) => {
      const next = normalizeTarget(to, state);
      navigate(next.to, { replace: true, state: next.state });
    },
    [navigate]
  );

  const go = useCallback(
    (delta: number) => {
      navigate(delta);
    },
    [navigate]
  );

  const listen = useCallback(
    (listener: (nextLocation: typeof location) => void) => {
      listenersRef.current.add(listener);

      return () => {
        listenersRef.current.delete(listener);
      };
    },
    []
  );

  return useMemo(
    () => ({
      go,
      listen,
      location: compatLocation,
      push,
      replace,
    }),
    [compatLocation, go, listen, push, replace]
  );
};
